import classNames from 'classnames';
import React, { forwardRef } from 'react';
import type { GenericUIProps, TransformedCSSProperties } from '../component.types';
import { transformSpecialCSSProperties } from '../utils';
import styles from './Box.module.scss';

export function Spacer() {
  return <div className={styles.spacer}></div>;
}

type SeparatorProps = React.HTMLAttributes<HTMLElement> & {
  /** Beware `horizontal` often has difficulty knowing it's own height, so `height` might need to be forced */
  orientation?: 'horizontal' | 'vertical';
};

export function Separator({ orientation = 'horizontal', ...rest }: SeparatorProps) {
  return (
    <div
      {...rest}
      className={classNames(styles.separator, styles[orientation], rest.className)}
    ></div>
  );
}

export interface BoxProps extends GenericUIProps, TransformedCSSProperties {}

export const Column = forwardRef((props: BoxProps, ref) => {
  const { className, as: Component = 'div', ...finalProps } = transformSpecialCSSProperties(props);
  return <Component {...finalProps} ref={ref} className={classNames(styles.column, className)} />;
});

export const Row = forwardRef((props: BoxProps, ref) => {
  const { className, as: Component = 'div', ...finalProps } = transformSpecialCSSProperties(props);
  return <Component {...finalProps} ref={ref} className={classNames(styles.row, className)} />;
});

export function Grid(props: BoxProps) {
  const { className, as: Component = 'div', ...finalProps } = transformSpecialCSSProperties(props);
  return <Component {...finalProps} className={classNames(styles.grid, className)} />;
}

/**
 * (Flex)Box offers the most Flex use-cases within the UI library.
 * If you require a lot of overrides, this probably isn't what you're looking for.
 */

export const Box = Object.assign(Row, {
  Spacer,
  Separator,
  Column,
  Row,
  Grid,
});

Box.displayName = 'Box';
Box.Column.displayName = 'Box.Column';
Box.Row.displayName = 'Box.Row';

export default Box;
